import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowUpRight, Check, ChevronDown, Tag, Sparkles, Building2,
  Calendar, Wrench, Briefcase, Zap, Layers, Eye
} from 'lucide-react';
import { faqSchema, breadcrumb, injectSchema } from '@/lib/schemas';

/**
 * AVX — ALLCOM Visual Experience
 * Ruta: /soluciones/visual-experience
 * Estética: ALLCOM Premium B&W con acentos cyan/teal de la paleta corporativa.
 * Glassmorphism + bordes metálicos en hover. Tipografía Syne + Inter.
 */

const PRODUCTS = [
  {
    id: 'shelf-strips',
    tag: 'Retail Intelligence',
    icon: Tag,
    title: 'Digital Shelf Strips',
    subtitle: 'Estanterías inteligentes con LED ultra-delgado.',
    image: '/avx/avx-shelf-strips.png',
    imageAlt: 'Estantería de retail con franjas LED mostrando precios dinámicos',
    specs: [
      'Factor de forma ultra-delgado (20mm)',
      'Alto brillo para salas de venta (≥1.500 nits)',
      'Integración nativa con motor de precios dinámicos ALLCOM',
      'Refresco 1.920Hz · sin parpadeo bajo cámaras',
    ],
    benefits: [
      'Actualización promocional instantánea en toda la red',
      'Eliminación de errores en precios y mermas asociadas',
      'Captura la atención en el momento crítico de compra',
      'Animaciones contextuales según hora y stock',
    ],
  },
  {
    id: 'rental-elite',
    tag: 'Corporate Events',
    icon: Sparkles,
    title: 'Rental Elite Series',
    subtitle: 'Pantallas LED de gran formato para eventos C-Level.',
    image: '/avx/avx-rental-elite.png',
    imageAlt: 'Pantalla LED de gran formato en escenario corporativo de alta gama',
    specs: [
      'Pixel pitch P2.6 · resolución 4K HDR',
      'Frecuencia de actualización 3.840Hz (anti-flicker en cámaras)',
      'Módulos rápidos: montaje en 4–6 horas',
      'Procesamiento Novastar / Brompton certificado',
    ],
    benefits: [
      'Calidad visual impecable para presentaciones ejecutivas',
      'Montaje rápido para lanzamientos de alto perfil',
      'Experiencia inmersiva que eleva el valor de marca',
      'Servicio integral: arriendo, operación y soporte 24/7',
    ],
  },
  {
    id: 'transparent-led',
    tag: 'Premium Spaces',
    icon: Building2,
    title: 'Architectural Transparent LED',
    subtitle: 'Fachadas y vitrinas con contenido digital sin perder luz.',
    image: '/avx/avx-transparent-led.png',
    imageAlt: 'Cafetería mid-market con panel LED transparente integrado en la vitrina',
    specs: [
      'Transparencia modular hasta 80%',
      'Sin cableado visible · diseño estructural liviano',
      'Pixel pitch P3.9–P10.4 · interior y semi-exterior',
      'Control remoto + telemetría 24/7 desde Control Panel ALLCOM',
    ],
    benefits: [
      'Convierte vitrinas y fachadas en activos digitales',
      'Conserva luz natural y visibilidad hacia el interior',
      'Ideal para cafeterías, retail mid-market y flagship stores',
      'Bajo consumo y mantenimiento mínimo',
    ],
  },
];

const CTA_OPTIONS = [
  {
    id: 'demo-virtual',
    icon: Calendar,
    title: 'Agendar Demo Virtual',
    desc: 'Acceso a showroom digital 360° con tour guiado por un especialista AVX.',
    cta: 'Agendar 30 min',
    href: '/contacto?topic=avx-demo-virtual',
  },
  {
    id: 'visita-tecnica',
    icon: Wrench,
    title: 'Solicitar Visita Técnica',
    desc: 'Validación de factibilidad en terreno por ingenieros especialistas en LED y obras civiles.',
    cta: 'Solicitar visita',
    href: '/contacto?topic=avx-visita-tecnica',
  },
  {
    id: 'consultor-avx',
    icon: Briefcase,
    title: 'Reunión con Consultor AVX',
    desc: 'Consultoría para proyectos de integración a gran escala: arquitectura, contenido, operación.',
    cta: 'Hablar con consultor',
    href: '/contacto?topic=avx-consultor',
  },
];

const FAQS = [
  {
    q: '¿Qué hace diferente a AVX de un proveedor LED tradicional?',
    a: 'AVX integra el hardware LED con el software de ALLCOM (Control Panel, Analytics, motor de precios dinámicos) y operación 24/7. No vendemos pantallas: entregamos un ecosistema visual conectado a tu data y a tus decisiones de negocio.',
  },
  {
    q: '¿Cuál es el plazo de implementación típico?',
    a: 'Digital Shelf Strips: 2–4 semanas por tienda según escala. Rental Elite: armado en 4–6 horas. Transparent LED arquitectónico: 4–8 semanas incluyendo obra civil y validación estructural.',
  },
  {
    q: '¿Ofrecen modelo OPEX (arriendo) o solo venta?',
    a: 'Ambos. Para Rental Elite el modelo natural es arriendo por evento. Para Shelf Strips y Transparent LED ofrecemos compra directa, leasing y as-a-Service con cuota mensual que incluye operación y mantención.',
  },
  {
    q: '¿Las pantallas se gestionan desde una sola plataforma?',
    a: 'Sí. Todo el portafolio AVX se administra desde Control Panel ALLCOM: contenido, programación, telemetría, alertas, reportes de impresiones y diagnóstico remoto.',
  },
  {
    q: '¿Qué soporte hay después de la instalación?',
    a: 'Soporte 24/7 con SLA garantizado, técnicos en terreno a nivel nacional, repuestos en stock y monitoreo proactivo desde nuestro NOC. Para Rental Elite, técnico dedicado durante el evento.',
  },
  {
    q: '¿Pueden integrarse con mi ERP / sistema de marketing actual?',
    a: 'Sí. Integramos vía APIs REST, conectores nativos con SAP, Oracle, Microsoft, plataformas de DOOH y CMS de marketing. La integración es parte del scope estándar de un proyecto AVX.',
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.21, 0.61, 0.35, 1] } },
};

export default function AVXPage() {
  // Inject FAQPage + Breadcrumb schema for AI engines / Google rich results
  useEffect(() => {
    injectSchema([
      breadcrumb([
        { name: 'Inicio', path: '/' },
        { name: 'Soluciones', path: '/soluciones' },
        { name: 'Visual Experience (AVX)', path: '/soluciones/visual-experience' },
      ]),
      faqSchema(FAQS),
    ]);
  }, []);

  return (
    <div className="bg-[#050E1F] text-white" data-testid="avx-page" style={{ fontFamily: 'Inter, sans-serif' }}>
      {/* HERO */}
      <AVXHero />

      {/* INTRO STRIP */}
      <AVXIntro />

      {/* PRODUCTS PORTFOLIO */}
      <section className="border-t border-white/5" data-testid="avx-portfolio">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32">
          <div className="mb-16 max-w-2xl">
            <p className="text-xs font-mono uppercase tracking-[0.32em] text-[#0EA5E9] mb-4" data-testid="avx-portfolio-eyebrow">
              Portafolio AVX
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight mb-4 leading-tight" style={{ fontFamily: 'Syne, sans-serif' }}>
              Tres soluciones, un mismo ecosistema visual.
            </h2>
            <p className="text-base text-white/60 leading-relaxed">
              Hardware LED de vanguardia + software ALLCOM + operación 24/7. Cada solución resuelve un escenario distinto del mundo retail, eventos y arquitectura corporativa.
            </p>
          </div>

          <div className="space-y-12">
            {PRODUCTS.map((p, i) => <AVXProductCard key={p.id} product={p} index={i} />)}
          </div>
        </div>
      </section>

      {/* CTA OPTIONS */}
      <section className="border-t border-white/5 bg-gradient-to-b from-[#050E1F] to-[#0A1628]" data-testid="avx-cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32">
          <div className="text-center mb-16 max-w-2xl mx-auto">
            <p className="text-xs font-mono uppercase tracking-[0.32em] text-[#0EA5E9] mb-4">Próximo paso</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight mb-4 leading-tight" style={{ fontFamily: 'Syne, sans-serif' }}>
              Solicitar Demo de Producto AVX
            </h2>
            <p className="text-base text-white/60 leading-relaxed">
              Elige cómo quieres conocer AVX. Te respondemos en menos de 24 horas hábiles.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="avx-cta-grid">
            {CTA_OPTIONS.map((opt) => <AVXCtaCard key={opt.id} option={opt} />)}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <AVXFAQs />

      {/* FINAL CTA STRIP */}
      <section className="border-t border-white/5" data-testid="avx-final-strip">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <h3 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight mb-6 leading-tight max-w-3xl mx-auto" style={{ fontFamily: 'Syne, sans-serif' }}>
            ¿Tu marca merece una experiencia visual a la altura?
          </h3>
          <p className="text-base text-white/60 max-w-xl mx-auto mb-10 leading-relaxed">
            Conversemos sobre tu proyecto. Diseñamos la arquitectura visual completa, la operamos por ti y la conectamos con tu data.
          </p>
          <Link
            to="/contacto?topic=avx"
            className="inline-flex items-center gap-2 px-7 py-3.5 bg-[#0EA5E9] hover:bg-[#0284C7] text-white text-sm font-semibold rounded-full transition-colors shadow-lg shadow-[#0EA5E9]/20"
            data-testid="avx-final-cta"
          >
            Iniciar proyecto AVX <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}

// ===== HERO =====
const AVXHero = () => (
  <section className="relative overflow-hidden" data-testid="avx-hero">
    {/* Abstract refracted-light background */}
    <div className="absolute inset-0">
      <img
        src="https://images.unsplash.com/photo-1550745165-9bc0b252726f?auto=format&fit=crop&w=2400&q=80"
        alt=""
        className="w-full h-full object-cover opacity-40"
        loading="eager"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#050E1F]/70 via-[#050E1F]/85 to-[#050E1F]" />
      <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full opacity-30 blur-3xl" style={{ background: 'radial-gradient(circle, rgba(14,165,233,0.6) 0%, transparent 70%)' }} />
      <div className="absolute bottom-0 left-0 w-[600px] h-[600px] rounded-full opacity-20 blur-3xl" style={{ background: 'radial-gradient(circle, rgba(248,217,15,0.4) 0%, transparent 70%)' }} />
    </div>

    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32 lg:py-40">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="inline-flex items-center gap-2 px-4 py-1.5 mb-8 rounded-full bg-white/5 backdrop-blur-xl border border-white/10"
        data-testid="avx-hero-badge"
      >
        <Eye className="w-3.5 h-3.5 text-[#0EA5E9]" strokeWidth={1.5} />
        <span className="text-[11px] font-mono uppercase tracking-[0.28em] text-white/80">ALLCOM Visual Experience · AVX</span>
      </motion.div>

      <motion.h1
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.1 }}
        className="text-4xl sm:text-5xl lg:text-7xl font-semibold tracking-tight leading-[1.02] mb-8 max-w-5xl"
        style={{ fontFamily: 'Syne, sans-serif' }}
        data-testid="avx-hero-title"
      >
        Impacto visual de alta fidelidad,<br />
        <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#0EA5E9] to-[#7DD3FC]">impulsado por la inteligencia de ALLCOM.</span>
      </motion.h1>

      <motion.p
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="text-base sm:text-lg lg:text-xl text-white/70 leading-relaxed max-w-3xl mb-12"
      >
        Diseñamos ecosistemas visuales inteligentes que conectan su marca con sus audiencias mediante tecnología LED avanzada y gestión de datos en tiempo real.
      </motion.p>

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.3 }}
        className="flex flex-wrap gap-3"
      >
        <a
          href="#avx-portfolio"
          onClick={(e) => { e.preventDefault(); document.querySelector('[data-testid="avx-portfolio"]')?.scrollIntoView({ behavior: 'smooth' }); }}
          className="inline-flex items-center gap-2 px-6 py-3 bg-[#0EA5E9] hover:bg-[#0284C7] text-white text-sm font-semibold rounded-full transition-colors"
          data-testid="avx-hero-cta-portfolio"
        >
          Ver portafolio <ArrowUpRight className="w-4 h-4" />
        </a>
        <Link
          to="/contacto?topic=avx"
          className="inline-flex items-center gap-2 px-6 py-3 bg-white/5 backdrop-blur-xl border border-white/15 hover:border-[#0EA5E9]/60 text-white text-sm font-semibold rounded-full transition-all"
          data-testid="avx-hero-cta-contact"
        >
          Hablar con un especialista
        </Link>
      </motion.div>
    </div>
  </section>
);

// ===== INTRO STRIP =====
const AVXIntro = () => (
  <section className="border-y border-white/5 bg-white/[0.02]" data-testid="avx-intro">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
      {[
        { icon: Layers, label: 'Hardware', value: 'LED de vanguardia', desc: 'P2.6 a P10.4 · transparente y opaco' },
        { icon: Zap, label: 'Software', value: 'Control Panel ALLCOM', desc: 'Contenido, telemetría y alertas' },
        { icon: Sparkles, label: 'Inteligencia', value: 'Data en tiempo real', desc: 'Decisiones de negocio conectadas' },
        { icon: Wrench, label: 'Operación', value: '24/7 nacional', desc: 'SLA, terreno y NOC ALLCOM' },
      ].map((item, i) => {
        const Icon = item.icon;
        return (
          <motion.div
            key={item.label}
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ delay: i * 0.06 }}
            className="flex items-start gap-4"
            data-testid={`avx-intro-${i}`}
          >
            <div className="w-11 h-11 rounded-xl bg-white/5 backdrop-blur-xl border border-white/10 flex items-center justify-center shrink-0">
              <Icon className="w-5 h-5 text-[#0EA5E9]" strokeWidth={1.5} />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.24em] font-mono text-white/40 mb-1">{item.label}</div>
              <div className="text-base font-semibold text-white mb-1" style={{ fontFamily: 'Syne, sans-serif' }}>{item.value}</div>
              <div className="text-xs text-white/50 leading-relaxed">{item.desc}</div>
            </div>
          </motion.div>
        );
      })}
    </div>
  </section>
);

// ===== PRODUCT CARD =====
const AVXProductCard = ({ product, index }) => {
  const Icon = product.icon;
  const reverse = index % 2 === 1;

  return (
    <motion.article
      variants={fadeUp}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      className="group relative rounded-3xl overflow-hidden bg-white/[0.03] backdrop-blur-xl border border-white/10 hover:border-[#0EA5E9]/40 transition-all duration-500"
      data-testid={`avx-product-${product.id}`}
    >
      {/* Metallic hover edge */}
      <div className="absolute inset-0 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" style={{
        background: 'linear-gradient(135deg, rgba(14,165,233,0.15) 0%, transparent 30%, transparent 70%, rgba(125,211,252,0.1) 100%)',
      }} />

      <div className={`grid lg:grid-cols-2 gap-0 ${reverse ? 'lg:[&>div:first-child]:order-2' : ''}`}>
        {/* Image */}
        <div className="relative aspect-[4/3] lg:aspect-auto lg:min-h-[480px] overflow-hidden">
          <img
            src={product.image}
            alt={product.imageAlt}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050E1F]/80 via-transparent to-transparent" />
        </div>

        {/* Content */}
        <div className="relative p-8 sm:p-10 lg:p-14">
          <div className="flex items-center gap-2 mb-6">
            <Icon className="w-5 h-5 text-[#0EA5E9]" strokeWidth={1.5} />
            <span className="text-[10px] font-mono uppercase tracking-[0.28em] text-[#0EA5E9]">{product.tag}</span>
          </div>

          <h3 className="text-2xl sm:text-3xl lg:text-4xl font-semibold mb-3 leading-tight" style={{ fontFamily: 'Syne, sans-serif' }}>
            {product.title}
          </h3>
          <p className="text-base text-white/60 mb-8 leading-relaxed">{product.subtitle}</p>

          {/* Specs */}
          <div className="mb-8" data-testid={`avx-product-${product.id}-specs`}>
            <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-white/40 mb-3">Especificaciones técnicas</div>
            <ul className="space-y-2">
              {product.specs.map((s, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm text-white/80 leading-relaxed">
                  <Check className="w-4 h-4 text-[#0EA5E9] mt-0.5 shrink-0" strokeWidth={2.5} />
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Benefits */}
          <div data-testid={`avx-product-${product.id}-benefits`}>
            <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-white/40 mb-3">Beneficios estratégicos</div>
            <ul className="space-y-2">
              {product.benefits.map((b, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm text-white/80 leading-relaxed">
                  <Sparkles className="w-4 h-4 text-[#F8D90F] mt-0.5 shrink-0" strokeWidth={2} />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </motion.article>
  );
};

// ===== CTA CARD =====
const AVXCtaCard = ({ option }) => {
  const Icon = option.icon;
  return (
    <Link
      to={option.href}
      className="group relative rounded-2xl bg-white/[0.03] backdrop-blur-xl border border-white/10 hover:border-[#0EA5E9]/50 hover:bg-white/[0.06] p-8 transition-all duration-300"
      data-testid={`avx-cta-${option.id}`}
    >
      <div className="w-12 h-12 rounded-xl bg-[#0EA5E9]/10 border border-[#0EA5E9]/20 flex items-center justify-center mb-6 group-hover:bg-[#0EA5E9] group-hover:border-[#0EA5E9] transition-all">
        <Icon className="w-5 h-5 text-[#0EA5E9] group-hover:text-white transition-colors" strokeWidth={1.5} />
      </div>
      <h3 className="text-xl font-semibold mb-3 leading-snug" style={{ fontFamily: 'Syne, sans-serif' }}>{option.title}</h3>
      <p className="text-sm text-white/60 leading-relaxed mb-6">{option.desc}</p>
      <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0EA5E9] group-hover:gap-2.5 transition-all">
        {option.cta} <ArrowUpRight className="w-4 h-4" />
      </span>
    </Link>
  );
};

// ===== FAQs =====
const AVXFAQs = () => {
  const [openIdx, setOpenIdx] = useState(0);
  return (
    <section className="border-t border-white/5" data-testid="avx-faqs">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32">
        <div className="text-center mb-16 max-w-2xl mx-auto">
          <p className="text-xs font-mono uppercase tracking-[0.32em] text-[#0EA5E9] mb-4">Preguntas frecuentes</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight mb-4 leading-tight" style={{ fontFamily: 'Syne, sans-serif' }}>
            Lo que necesitas saber.
          </h2>
        </div>
        <div className="space-y-3" data-testid="avx-faqs-list">
          {FAQS.map((f, i) => {
            const open = openIdx === i;
            return (
              <div
                key={i}
                className={`rounded-2xl bg-white/[0.03] backdrop-blur-xl border transition-all ${open ? 'border-[#0EA5E9]/40' : 'border-white/10 hover:border-white/20'}`}
                data-testid={`avx-faq-${i}`}
              >
                <button
                  type="button"
                  onClick={() => setOpenIdx(open ? -1 : i)}
                  className="w-full flex items-center justify-between gap-4 text-left px-6 py-5"
                  data-testid={`avx-faq-toggle-${i}`}
                  aria-expanded={open}
                >
                  <span className="text-base sm:text-lg font-semibold text-white leading-snug" style={{ fontFamily: 'Syne, sans-serif' }}>{f.q}</span>
                  <ChevronDown className={`w-5 h-5 text-[#0EA5E9] shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
                </button>
                {open && (
                  <div className="px-6 pb-5 -mt-1 text-sm text-white/70 leading-relaxed" data-testid={`avx-faq-answer-${i}`}>
                    {f.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
